<template>
	<router-link
		@click.native="closeExpandedSmallNav()"
		:to="route"
		tag="button"
		:class="{
			expanded: expandedMenu,
			'router-link-exact-active': isParentRouteActive(route),
			disabled: disabled,
		}"
		:disabled="disabled"
		class="expanding-nav-item"
		data-cy="navigation-item"
	>
		<div class="menu-item-wrapper">
			<span
				class="icon"
				:class="{ compactIcon: !expandedMenu, disabled: disabled }"
				data-cy="navigation-icon"
			>
				<font-awesome-icon :icon="[iconWeight, icon]" />
			</span>
			<span
				class="menu-item-name"
				:class="{ disabled: disabled }"
				v-if="expandedMenu || brandBuilderNav"
				data-cy="navigation-name"
				>{{ name }}</span
			>
			<font-awesome-icon
			v-if="showSpinner"
			class="waiting-animation"
			data-testid="waiting-animation"
			:icon="['far', 'circle-notch']"
		/>
		</div>
	</router-link>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
	name: 'ExpandingNavItem',
	props: {
		icon: String,
		iconWeight: String,
		name: String,
		route: String,
		meta: Object,
	},
	computed: {
		...mapState({
			expandedMenu: (state) => state.menus.expandedMenu,
			brandBuilderNav: (state) => state.menus.brandBuilderNav,
			canPostSocial: (state) => {
				const permissionsList = state.preferenceCenterPermissions.permissionsList;
				return permissionsList.some((p) => p === 'AbilityToPostToSocial');
			},
			disabled(state) {
				if (this.meta.menuData.hasOwnProperty('disabled')) {
					if (this.meta.menuData.disabled) return true;
				}
	
				if (!this.isValidPreferences()()) {
					if (
						this.meta.menuData.hasOwnProperty('availableWithInvalidPreferences')
					)
						return !this.meta.menuData.availableWithInvalidPreferences;
					else return true;
				}
	
				return false;
			},
			showVideoReviewSpinner: (state) => state.menus.showVideoReviewSpinner,
		}),
		showSpinner() {
			if (this.meta.loadingSpinner !== undefined) {
				return this.showVideoReviewSpinner && this.expandedMenu;
			}
			return false;
		},
	},
	methods: {
		...mapGetters(['isValidPreferences']),
		...mapActions(['closeExpandedSmallNav']),
		isParentRouteActive(parentRoute) {
			const currentChildBaseRoute =
				this.$route.matched[0] !== undefined ? this.$route.matched[0].path : '';
			if (parentRoute === currentChildBaseRoute) {
				return true;
			}
			// This allows 'cousin' links to highlight the appropriate parent item in the navigation.
			if (
				currentChildBaseRoute.indexOf('marketing-resources-details') !== -1 &&
				parentRoute.indexOf('marketing-resources') !== -1
			) {
				return true;
			}
			if (
				currentChildBaseRoute.indexOf('client-connect-contact-list') !== -1 &&
				parentRoute.indexOf('client-connect') !== -1
			) {
				return true;
			}
			if (
				currentChildBaseRoute.indexOf('client-connect-activity-log') !== -1 &&
				parentRoute.indexOf('client-connect') !== -1
			) {
				return true;
			}
			if (
				currentChildBaseRoute.indexOf('campaign-editor-workspace') !== -1 &&
				parentRoute.indexOf('campaign-editor-home') !== -1
			) {
				return true;
			}
			if (
				currentChildBaseRoute.indexOf('google-analytics') !== -1 &&
				parentRoute.indexOf('analytics') !== -1
			) {
				return true;
			}
			if (
				currentChildBaseRoute.indexOf('salesforce-analytics') !== -1 &&
				parentRoute.indexOf('analytics') !== -1
			) {
				return true;
			}
			if (
				currentChildBaseRoute.indexOf('social-media-analytics') !== -1 &&
				parentRoute.indexOf('analytics') !== -1
			) {
				return true;
			}
			return false;
		},
	},
};
</script>

<style scoped lang="scss">
.expanding-nav-item {
	height: $context-nav-height;
	padding: $base-padding;
	box-sizing: border-box;
	text-align: center;
	border-top: none;
	border-bottom: $border-width solid $border-color-light;
	border-left: $b360-border-radius-small solid $off-white;
	border-right: none;
	cursor: pointer;
	background-color: transparent;
	font-size: $b360-font-size--base;
	@include typo--roboto__medium;
	color: $text-base-color;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;

	&:hover {
		border-left: $b360-border-radius-small solid $brand-blue;
		background-color: $highlight-color;
		color: $brand-blue;
		cursor: pointer;
	}

	&.disabled:hover {
		color: $b360-color--gray__medium;
		background: $b360-color--gray__header-bg;
	}

	&.router-link-exact-active {
		border-left: $b360-border-radius-small solid $brand-blue;
		background-color: $highlight-color;
	}

	.icon {
		color: $brand-blue;
		width: 40px;
		font-size: 30px;
	}

	.disabled {
		color: $b360-color--gray__medium;
		cursor: no-drop;
	}

	.compactIcon {
		width: auto;
	}

	&.bb-nav {
		.icon {
			margin-right: $base-padding;
		}

		.menu-item-name {
			display: block;
		}
	}

	.menu-item-name {
		display: none;
	}
}

.expanded {
	@include lg {
		width: $collapsed-menu-width;
		text-align: left;
	}
	.expanding-nav-item {
		.icon {
			display: flex;
			margin-right: $base-padding;
		}

		.menu-item-name {
			display: block;
		}
	}
}

.menu-item-wrapper {
	display: flex;
	align-items: center;
}

.waiting-animation {
	animation: spin 1.5s ease-in-out infinite;
	margin-left: 10px;
}
</style>
